/**
 * @file SiteAccessSection.tsx
 * @description Раздел «Доступ к сайту» в «Настройках сайта» (phase19.16,
 * SB138, SB55; uiux-site-closed.md §2, §7, §8): статус (StatusPill «успех» /
 * «внимание»), пояснение, «Закрыть сайт…» (вторичная кнопка с обводкой,
 * 48 px — открывает окно, а не переключает сразу), при закрытом сайте —
 * [Смотреть сайт] [Открыть сайт] в один клик, история пауз.
 *
 * Статус не получен — «Не удалось узнать, открыт ли сайт» с [Повторить],
 * закрытие недоступно. Без права настроек кнопки неактивны с пояснением.
 * Пауза платформы — открыть может только платформа.
 * phase19.28 (PL8, PL9): пауза «запуск» — «Магазин ещё не открыт» с
 * подсказкой; «Открыть сайт» сначала проверяет условия (`useLaunchOpen`)
 * и при нехватке показывает чек-лист.
 */

import { useState } from "react";

import { Button } from "../ui/button";
import { NoticeStrip } from "../ui/NoticeStrip";
import { Skeleton } from "../ui/skeleton";
import { StatusPill } from "../ui/StatusPill";
import { CloseSiteDialog } from "./CloseSiteDialog";
import { LaunchChecklistDialog } from "./LaunchChecklistDialog";
import { SiteClosureHistory } from "./SiteClosureHistory";
import { useShopContextKey, useSiteAccess } from "../../hooks/site-access/useSiteAccess";
import { useLaunchOpen } from "../../hooks/site-access/useLaunchOpen";
import { useSiteAccessActions } from "../../hooks/site-access/useSiteAccessActions";
import { SITE_ACCESS_NETWORK_TEXT } from "../../lib/site-access/site-access-errors";
import { safeTimeZone } from "../../lib/site-access/site-access-time";
import { activeSiteClosure, siteClosedBanner } from "../../lib/site-access/site-access-view";

/** Пояснение у открытого сайта (uiux §2). */
export const SITE_OPEN_EXPLANATION =
  "Закройте сайт, если меняете цены или каталог и покупатели не должны видеть промежуточное состояние.";

/** Статус-пилюля по источнику паузы (реестр, а не условие). */
const CLOSURE_PILL: Readonly<Record<"shop" | "platform" | "launch", string>> = {
  shop: "Сайт закрыт для покупателей",
  platform: "Сайт закрыт платформой",
  launch: "Магазин ещё не открыт",
};

/** Без права настроек. */
export const SITE_ACCESS_RIGHT_NOTE = "Закрывать и открывать сайт может владелец или сотрудник с правом настроек магазина.";

export function SiteAccessSection() {
  const access = useSiteAccess(useShopContextKey(), "load");
  const actions = useSiteAccessActions(access);
  const launch = useLaunchOpen(access);
  const [dialog, setDialog] = useState(false);
  const [retrying, setRetrying] = useState(false);

  /** «Повторить»: видно, что проверка идёт, повторные нажатия не копят запросы. */
  async function retry() {
    setRetrying(true);
    try {
      await access.reload();
    } finally {
      setRetrying(false);
    }
  }
  const { state } = access;

  if (!state) {
    if (!access.failed) {
      return (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-12 w-full sm:w-56" />
        </div>
      );
    }
    // Кнопка под полосой, а не в ней: на 360 текст в полосе не сжимается в столбик.
    return (
      <div className="flex flex-col gap-3">
        <NoticeStrip tone="error">{SITE_ACCESS_NETWORK_TEXT.load} Закрыть сайт сейчас нельзя.</NoticeStrip>
        <Button variant="outline" className="h-12 self-start" loading={retrying} disabled={retrying} onClick={() => void retry()}>
          {retrying ? "Проверяем…" : "Повторить"}
        </Button>
      </div>
    );
  }

  const now = access.serverNow();
  const closure = activeSiteClosure(state, now);
  const banner = siteClosedBanner(state, now);
  const zone = safeTimeZone(state.timeZone);
  const refreshKey = [state.shopClosure?.id, state.shopClosure?.autoOpenAt, state.platformClosure?.id, closure?.id].join(":");

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        {closure ? (
          <StatusPill tone="attention" className="self-start">{CLOSURE_PILL[closure.source]}</StatusPill>
        ) : (
          <StatusPill tone="success" className="self-start">Сайт открыт для покупателей</StatusPill>
        )}
        {banner?.kind === "platform" || banner?.kind === "launch" ? <p className="type-body text-on-surface">{banner.text}</p> : null}
        {banner?.kind === "shop" ? (
          <p className="type-body text-on-surface" data-testid="site-access-closed-info">
            С {banner.since}
            {banner.by ? ` · ${banner.by}` : ""} · {banner.elapsed} · {banner.autoOpen ? `откроется сам ${banner.autoOpen}` : "без срока"}
          </p>
        ) : null}
        {!closure ? <p className="type-body text-on-surface-variant">{SITE_OPEN_EXPLANATION}</p> : null}
      </div>

      {banner?.kind === "launch" ? (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" className="h-12" loading={actions.busy === "watch"} onClick={() => actions.watch()}>
            Смотреть сайт
          </Button>
          <Button className="h-12" disabled={!state.canManage} loading={launch.busy} onClick={() => void launch.start(banner.closureId)}>
            Открыть сайт
          </Button>
        </div>
      ) : null}
      {banner?.kind === "shop" ? (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" className="h-12" loading={actions.busy === "watch"} onClick={() => actions.watch()}>
            Смотреть сайт
          </Button>
          <Button className="h-12" disabled={!state.canManage} loading={actions.busy === "open"} onClick={() => void actions.open(banner.closureId)}>
            Открыть сайт
          </Button>
        </div>
      ) : null}
      {!closure ? (
        <Button variant="outline" className="h-12 w-full sm:w-auto sm:self-start" disabled={!state.canManage} onClick={() => setDialog(true)}>
          Закрыть сайт…
        </Button>
      ) : null}
      {!state.canManage && banner?.kind !== "platform" ? <p className="type-caption text-on-surface-variant">{SITE_ACCESS_RIGHT_NOTE}</p> : null}

      <SiteClosureHistory zone={zone} refreshKey={refreshKey} />
      {state.canManage ? <CloseSiteDialog open={dialog} onClose={() => setDialog(false)} access={access} /> : null}
      <LaunchChecklistDialog launch={launch} />
    </div>
  );
}
